import React, { useState } from 'react';
import styles from './GroupSelectorModal.module.css';
import { FloComponentProps } from 'components';
import { FloModal } from 'components';
import { useTranslation } from 'react-i18next';
import { FloModalBodyProps } from 'components';
import { FloSearchBox } from 'components/FloSearchBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Row, Col, Modal } from 'react-bootstrap';
import classnames from 'classnames';


export type GroupOptions = GroupOption[];

export interface GroupOption {
  value:      string;
  text:       string;
  disabled?:  boolean;
}

type GroupSelectorRowProps = {
  label: string;
  isSelected: boolean;
  isLast: boolean;
  onClick: () => void;
  disabled: boolean;
}

const GroupSelectorRow: React.FC<GroupSelectorRowProps> = React.memo(({label, isSelected, isLast, onClick, disabled}) => (
  <>
    <Row className={classnames(styles.optionRow, {[styles.activeRow]: !disabled})} onClick={onClick}>
      <Col xs="6"><span>{label}</span></Col>
      <Col xs="6" className={styles.iconWrapper}>{isSelected ? <FontAwesomeIcon className={styles.icon} icon="check" /> : null }</Col>
    </Row>
    { !isLast && <Row className={styles.dividerWrapper}><Col xs="12" className={styles.divider}></Col></Row> }
  </>
))

interface GroupSelectorBodyProps extends FloModalBodyProps {
  options:            GroupOptions;
  onSelect:           (value: string) => void;
  hasMore?:           boolean;
  page?:              number;
  isLoading?:         boolean;
  onLoadOptions?:     (search: string | undefined, page: number) => void;
  searchPlaceholder?: string;
}

const GroupSelectorBody: React.FC<GroupSelectorBodyProps> = React.memo(({options, bindIsFormValid, bindSubmitForm, onSelect, hasMore, isLoading, onLoadOptions, page, searchPlaceholder}) => {
  const { t } = useTranslation();

  const [ search, setSearch ] = useState('');
  const [ selectedValue, setSelectedValue ] = useState('');
  const [ loadingNextPage, setLoadingNextPage ] = useState(false);

  const handleSearchChange = (value: string) => {
    setLoadingNextPage(false);
    setSearch(value);
    setSelectedValue('');
    if (onLoadOptions && (value.trim().length > 2 || value.trim() === '')) {
      onLoadOptions(value, 1);
    }
  }

  const handleNext = () => {
    setLoadingNextPage(true);
    if (onLoadOptions && page) {
      onLoadOptions(search, page + 1);
    }
  };

  bindSubmitForm(async () => {
    onSelect(selectedValue);
  });

  bindIsFormValid(selectedValue !== '');

  return (
    <div className={styles.bodyContainer}>
      <FloSearchBox
        className={styles.searchBox}
        value={search}
        placeholder={searchPlaceholder || t('Search')}
        onChange={handleSearchChange}
      />
      { (isLoading && !loadingNextPage) ? <div className={styles.loaderMain}><span className="spinner-border" role="status" aria-hidden="true"></span></div> :
        <Row className={styles.itemsContainer}>
          <Col xs="12">
            <InfiniteScroll
              className       = {styles.infiniteScroll}
              height          = {250}
              dataLength      = {options.length}
              next            = {handleNext}
              hasMore         = {hasMore || false}
              loader          = {<div className={styles.loaderNewPage}><span className="spinner-border" role="status" aria-hidden="true"></span></div>}
            >
              { options.map((option, ind) => (
                <GroupSelectorRow
                  key         = {option.value}
                  label       = {option.text}
                  isSelected  = {option.value === selectedValue}
                  isLast      = {options.length === ind + 1}
                  onClick     = {() => { if (!option.disabled) { setSelectedValue(option.value) }}}
                  disabled    = {option.disabled || false}
                />)
              )}
              { options.length === 0 && <div className={styles.noOptions}>{t('no-options-found')}</div> }
            </InfiniteScroll>
          </Col>
        </Row>
      }
    </div>
  )
})

export interface GroupSelectorModalProps extends FloComponentProps {
  show:               boolean;
  onHide:             () => void;
  title:              string;
  subtitle:           string,
  options:            GroupOptions;
  onSelect:           (value: string) => void;
  hasMore?:           boolean;
  page?:              number;
  isLoading?:         boolean;
  onLoadOptions?:     (search: string | undefined, page: number) => void;
  searchPlaceholder?: string;
}

export const GroupSelectorModal: React.FC<GroupSelectorModalProps> = React.memo(({title, options, onSelect, subtitle, onHide, hasMore, isLoading, page, onLoadOptions, searchPlaceholder, ...rest}) => {
  return (
    <FloModal
      {...rest}
      onHide          = {onHide}
      name            = "group-selector"
      title           = {title}
      body            = { (props) =>
        <GroupSelectorBody
          {...props}
          options           = {options}
          hasMore           = {hasMore}
          page              = {page}
          isLoading         = {isLoading}
          onLoadOptions     = {onLoadOptions}
          searchPlaceholder = {searchPlaceholder}
          onSelect          = {(value) => {
            onSelect(value);
            onHide();
          }}
        />
      }
      header          = { (props) =>
        <div className={styles.footer}>
          <Modal.Title id={'title-header'}>{title}</Modal.Title>
          <div className={styles.footerSub}>{subtitle}</div>
        </div>
      }
    />
  );
})

export default GroupSelectorModal;
